import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Lock, ShieldCheck, Clock, Phone } from 'lucide-react';
export function Hero() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  return <section className="relative bg-slate-50 pt-12 pb-16 lg:pt-20 lg:pb-24 overflow-hidden" data-id="element-52">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-53">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-blue-100/40 blur-3xl" data-id="element-54" />
        <div className="absolute bottom-[10%] -left-[10%] w-[50%] h-[50%] rounded-full bg-slate-200/50 blur-3xl" data-id="element-55" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-56">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-57">
          {/* Left Column - Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7 xl:col-span-7" data-id="element-58">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-brand-blue text-sm font-medium mb-6" data-id="element-59">
              <span className="relative flex h-2 w-2" data-id="element-60">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-blue opacity-75" data-id="element-61"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-blue" data-id="element-62"></span>
              </span>
              Same-Day Auto Glass Openings Available
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight text-navy-900 mb-6 leading-tight" data-id="element-63">
              Auto Glass Repair &{' '}
              <span className="text-brand-blue" data-id="element-64">Windshield Replacement</span> in
              Southeast Michigan
            </h1>

            <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-2xl" data-id="element-65">
              Cracked windshield, shattered side window, or broken rear glass?
              Kosin's Glass provides free auto glass estimates, certified
              installation, quality glass that meets or exceeds OEM standards,
              and help working with most insurance companies.
            </p>

            <ul className="space-y-4 mb-8" data-id="element-66">
              {['Free auto glass estimates — no obligation', 'Windshield replacement, chip repair, side glass, door glass and rear window replacement', 'Certified technicians for cars, trucks, vans, fleets and semi trucks', 'Lifetime vehicle glass workmanship warranty'].map((bullet, i) => <motion.li key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.3,
              delay: 0.2 + i * 0.1
            }} className="flex items-start gap-3" data-id="element-67">
                  <CheckCircle2 className="w-6 h-6 text-brand-blue shrink-0 mt-0.5" data-id="element-68" />
                  <span className="text-slate-700 font-medium" data-id="element-69">{bullet}</span>
                </motion.li>)}
            </ul>

            <div className="flex items-center gap-2 text-sm text-slate-500 bg-white/60 inline-flex px-4 py-2 rounded-lg border border-slate-200/60 backdrop-blur-sm" data-id="element-70">
              <Lock className="w-4 h-4 text-slate-400" data-id="element-71" />
              <span data-id="element-72">
                Your information is secure. Kosin's Glass will never share your
                details.
              </span>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-5 xl:col-span-5" id="quote" data-id="element-73">
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden relative" data-id="element-74">
              <div className="absolute top-0 left-0 w-full h-1 bg-brand-blue" data-id="element-75"></div>

              <div className="p-6 sm:p-8" data-id="element-76">
                {!isSubmitted ? <>
                    <div className="inline-block px-3 py-1 bg-brand-light text-brand-blue text-xs font-bold uppercase tracking-wider rounded-md mb-4" data-id="element-77">
                      Free Quote
                    </div>
                    <h2 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-78">
                      Request a Free Auto Glass Quote
                    </h2>
                    <p className="text-sm text-slate-600 mb-6" data-id="element-79">
                      Submit your details and a Kosin's Glass team member will
                      contact you to confirm your vehicle details, availability
                      and insurance information.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4" data-id="element-80">
                      <div data-id="element-81">
                        <label htmlFor="fullName" className="block text-sm font-semibold text-navy-900 mb-1.5" data-id="element-82">
                          Full Name
                        </label>
                        <input type="text" id="fullName" name="fullName" required placeholder="John Smith" autoComplete="name" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-colors outline-none text-navy-900 placeholder:text-slate-400" data-id="element-83" />
                      </div>

                      <div data-id="element-84">
                        <label htmlFor="phone" className="block text-sm font-semibold text-navy-900 mb-1.5" data-id="element-85">
                          Phone Number
                        </label>
                        <input type="tel" id="phone" name="phone" required placeholder="(555) 123-4567" autoComplete="tel" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-colors outline-none text-navy-900 placeholder:text-slate-400" data-id="element-86" />
                      </div>

                      <div data-id="element-87">
                        <label htmlFor="email" className="block text-sm font-semibold text-navy-900 mb-1.5" data-id="element-88">
                          Email Address
                        </label>
                        <input type="email" id="email" name="email" required placeholder="john@email.com" autoComplete="email" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-colors outline-none text-navy-900 placeholder:text-slate-400" data-id="element-89" />
                      </div>

                      <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 text-xs text-slate-600 flex items-start gap-2" data-id="element-90">
                        <ShieldCheck className="w-4 h-4 text-brand-blue shrink-0 mt-0.5" data-id="element-91" />
                        <p data-id="element-92">
                          Kosin's will ask for vehicle details when they call to
                          ensure accuracy.
                        </p>
                      </div>

                      <button type="submit" className="w-full bg-brand-blue hover:bg-brand-hover text-white font-bold text-lg py-4 px-6 rounded-xl transition-colors shadow-lg shadow-brand-blue/20 flex justify-center items-center gap-2 group" data-id="element-93">
                        Request My Free Quote
                        <span className="group-hover:translate-x-1 transition-transform" data-id="element-94">
                          →
                        </span>
                      </button>
                    </form>

                    <div className="mt-6 text-center" data-id="element-95">
                      <p className="text-sm text-slate-600 mb-2" data-id="element-96">
                        Prefer to call?
                      </p>
                      <a href="tel:517-545-7772" className="inline-flex items-center gap-2 text-navy-900 font-bold hover:text-brand-blue transition-colors" data-id="element-97">
                        <Phone className="w-4 h-4" data-id="element-98" />
                        Howell Auto Glass Service Center: 517-545-7772
                      </a>
                    </div>

                    <div className="mt-6 pt-4 border-t border-slate-100 text-center" data-id="element-99">
                      <p className="text-xs text-slate-500" data-id="element-100">
                        We respect your privacy. Your details are safe with us.
                      </p>
                    </div>
                  </> : <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="py-12 text-center" data-id="element-101">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-102">
                      <CheckCircle2 className="w-8 h-8" data-id="element-103" />
                    </div>
                    <h3 className="text-2xl font-bold text-navy-900 mb-3" data-id="element-104">
                      Quote Request Received!
                    </h3>
                    <p className="text-slate-600 mb-8" data-id="element-105">
                      Thank you. A Kosin's Glass team member will contact you
                      shortly to confirm your vehicle details and provide your
                      free estimate.
                    </p>
                    <div className="bg-slate-50 p-4 rounded-xl text-sm text-slate-700" data-id="element-106">
                      <p className="font-medium mb-2" data-id="element-107">
                        Need immediate assistance?
                      </p>
                      <a href="tel:517-545-7772" className="text-brand-blue font-bold hover:underline flex items-center justify-center gap-2" data-id="element-108">
                        <Phone className="w-4 h-4" data-id="element-109" /> Call 517-545-7772
                      </a>
                    </div>
                  </motion.div>}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}